/**
 * Competitive Positioning Section
 * Shows how Parcel Lead Pro compares to alternatives
 * Uses the "multiply by 10" psychology from the CPO spec
 */

import { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Check, X, Clock, DollarSign, Search, Map, FileText } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const comparisons = [
  {
    method: 'County GIS Websites',
    icon: Map,
    cost: 'Free',
    time: '8-12 hrs',
    color: '#ef4444',
    pros: ['Free to access'],
    cons: [
      'Must visit each county site separately',
      'No investment scoring',
      'No growth data',
      'No infrastructure overlays',
      'Manual data export only',
      'No alerts or monitoring',
    ],
  },
  {
    method: 'Realtor / Broker',
    icon: FileText,
    cost: '$3,000-15,000',
    time: '1-4 weeks',
    color: '#f97316',
    pros: ['Local expertise', 'Access to MLS listings'],
    cons: [
      'Realtors work for sellers, not you',
      'No off-market parcel data',
      'Limited to their network',
      'Biased toward quick commissions',
      'No AI scoring or analytics',
    ],
  },
  {
    method: 'Traditional Research',
    icon: Search,
    cost: 'Your time ($50-150/hr)',
    time: '10-20 hrs/county',
    color: '#f59e0b',
    pros: ['Comprehensive if thorough'],
    cons: [
      'Census data alone is 6-9 months stale',
      'No AI scoring or predictions',
      'Must build spreadsheets manually',
      'No real-time alerts',
      'Hard to compare counties',
    ],
  },
  {
    method: 'Parcel Lead Pro',
    icon: DollarSign,
    cost: '$79/mo',
    time: '15 min',
    color: '#7CFF6B',
    isUs: true,
    pros: [
      'Verified South Carolina coverage, expanding to additional states',
      'Parcel analysis on verified infrastructure data',
      '7 growth signals per parcel',
      'Infrastructure + growth overlays',
      'Investment Memo in 1 click',
    ],
  },
];

export default function CompetitivePositioning() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const items = section.querySelectorAll('.comp-animate');
    const tl = gsap.timeline({ scrollTrigger: { trigger: section, start: 'top 75%', once: true } });
    tl.fromTo(items, { y: 30, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6, stagger: 0.1, ease: 'power3.out' });
    return () => { tl.kill(); };
  }, []);

  return (
    <section id="why-parcel-lead-pro" ref={sectionRef} className="relative py-20 md:py-28" style={{ background: '#0B1420' }}>
      <div className="mx-auto max-w-[1200px] px-6">
        {/* Header */}
        <div className="comp-animate mb-12 text-center">
          <span className="label-accent mb-4 block" style={{ color: '#7CFF6B' }}>WHY PARCEL LEAD PRO</span>
          <h2 className="font-display font-semibold" style={{ fontSize: 'clamp(32px, 4vw, 48px)', lineHeight: 1.1, color: '#F7F8F4' }}>
            40× faster than doing it yourself.
          </h2>
          <p className="font-body mx-auto mt-4" style={{ fontSize: 17, color: '#a9b4c0', maxWidth: 600 }}>
            What takes land investors 10–20 hours per county, Parcel Lead Pro does in 15 minutes.
            This is not a marketing claim — this is the math.
          </p>
        </div>

        {/* Comparison cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {comparisons.map((item, i) => {
            const Icon = item.icon;
            return (
              <div
                key={i}
                className={`comp-animate rounded-2xl border p-5 ${item.isUs ? 'ring-1' : ''}`}
                style={{
                  background: item.isUs ? 'rgba(199,164,91,0.06)' : '#101D29',
                  borderColor: item.isUs ? '#7CFF6B' : 'rgba(247,248,244,0.08)',
                  outlineColor: item.isUs ? '#7CFF6B' : 'transparent',
                }}
              >
                {/* Method name + cost/time */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: `${item.color}15` }}>
                    <Icon size={14} style={{ color: item.color }} />
                  </div>
                  <div>
                    <p className={`text-xs font-black ${item.isUs ? 'text-[#7CFF6B]' : 'text-[#F7F8F4]'}`}>{item.method}</p>
                    <div className="flex items-center gap-2 text-[10px]" style={{ color: '#6B7B8D' }}>
                      <span className="flex items-center gap-1"><DollarSign size={8} />{item.cost}</span>
                      <span className="flex items-center gap-1"><Clock size={8} />{item.time}</span>
                    </div>
                  </div>
                </div>

                {/* Pros / Cons */}
                <div className="space-y-1.5">
                  {(item.isUs ? item.pros : [...item.pros, ...item.cons!]).map((point, j) => {
                    const isCon = !item.isUs && j >= item.pros.length;
                    return (
                      <div key={j} className="flex items-start gap-1.5">
                        {item.isUs || !isCon ? (
                          <Check size={10} className="text-emerald-400 shrink-0 mt-0.5" />
                        ) : (
                          <X size={10} className="text-red-400 shrink-0 mt-0.5" />
                        )}
                        <span className={`text-[11px] ${isCon ? 'line-through' : ''}`} style={{ color: isCon ? '#6B7B8D' : '#A8B8AE' }}>{point}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* ROI Callout */}
        <div className="comp-animate rounded-2xl border border-[#7CFF6B]/20 p-6 text-center" style={{ background: 'rgba(199,164,91,0.04)' }}>
          <p className="text-sm font-black text-[#F7F8F4] mb-2">The Math Is Simple</p>
          <p className="text-xs max-w-2xl mx-auto" style={{ color: '#A8B8AE' }}>
            At $50/hour, 10 hours of manual research per county costs <span className="text-[#F7F8F4] font-bold">$500</span>.
            Parcel Lead Pro Investor is <span className="text-[#7CFF6B] font-bold">$79/month</span> for unlimited counties.
            <span className="block mt-2 text-[#7CFF6B] font-bold">One profitable deal pays for 10+ years of the platform.</span>
          </p>
        </div>
      </div>
    </section>
  );
}
